/* eslint-disable max-lines */
import { useMemo } from 'react';
import { useBreakpoints } from '../../../../../../bookings/util/useBreakpoints';
import {
  LinkTrackingData,
  SearchCardType,
  TrackingDataEvent,
} from '../../../../../../common/AnalyticsContext/LinkTracking';
import { useAnalytics } from '../../../../../../common/AnalyticsContext/useAnalytics';
import { useSearchFilters } from '../../../../../../common/components/SearchHeader/filters/useSearchFilters';
import { COVID_19_FLU_VACCINATIONS } from '../../../../../../common/components/SearchHeader/types/FilterChoice';
import { HeImgix } from '../../../../../../common/components/images/HeImgix';
import { addQueryParams } from '../../../../../../common/routing/addQueryParams';
import { PracticeTag } from '../../../../../../common/schema/hannibal/types.generated';
import { filterNulls } from '../../../../../../common/util/filterNulls';
import { BUPA_PLATINUM, PROVIDERS } from '../../../../../PreferredProviderSection/constants';
import { SearchResultPractice } from '../../../../../types';
import { makePracticeContactUrl } from '../../../../../urls';
import { Address } from '../Address/Address';
import { Appointments } from '../Appointments/Appointments';
import { DentalPricing } from '../DentalPricing/DentalPricing';
import { DentalPricingGapFree } from '../DentalPricing/DentalPricingGapFree/DentalPricingGapFree';
import { getDentalPricingBracket } from '../DentalPricing/getDentalPricing';
import { Logo } from '../Logo/Logo';
import { ProfileName } from '../ProfileName/ProfileName';
import { Subheading } from '../Subheading/Subheading';
import { Card, CardBody, ImageContainer, LogoFrame } from '../card.styles';
import { PracticeDetails } from '../details/PracticeDetails/PracticeDetails';
import { tagFactory } from '../details/Tag/tagFactory';
import { FeaturedTag } from './FeaturedTag/FeaturedTag';
import { Layout } from './Layout';
import { formatSpecialties } from './formatSpecialties';

export interface PracticeCardProps {
  index?: number;
  practice: SearchResultPractice;
  searchSpecialty?: string;
  resultType?: 'standard' | 'extended';
  inSearchedSuburb?: boolean;
}

type UrlContext = {
  specialty?: string;
  vaccineType?: string;
};

export const PracticeCard = ({
  practice,
  index,
  searchSpecialty,
  resultType,
  inSearchedSuburb,
}: PracticeCardProps) => {
  const {
    id,
    specialties,
    tags,
    name,
    address,
    image,
    appointmentSummary,
    timezone,
    phoneNumber,
    interests,
    preferredProviders,
    featured,
    url,
    bookingBar,
    treatments,
    preferredProviderPrograms,
    rankingInfo,
    logo,
  } = practice;

  const [filters] = useSearchFilters();

  const vaccineType =
    filters.topic?.specialist === COVID_19_FLU_VACCINATIONS ? filters.vaccineType : undefined;

  const clickTracking: LinkTrackingData = {
    event: TrackingDataEvent.SEARCH_RESULT_CARD,
    searchCardType: SearchCardType.PRACTICE,
    searchCardBulkBilling: tags.includes(PracticeTag.BulkBilling),
    searchCardFeatured: featured,
    searchCardHasAppointments: appointmentSummary !== null,
    searchCardResultsPosition: (index || 0) + 1,
    searchCardPriceVerified: tags.includes(PracticeTag.PricingVerified),
    searchCardSpecialOffer: tags.includes(PracticeTag.SpecialOffer),
    searchResultIsInSuburb: inSearchedSuburb,
    searchResultType: resultType,
    practiceName: name,
    practiceId: id,
    practiceInterests: interests,
    practicePreferredProviders: preferredProviders,
  };

  const isMobile = useBreakpoints().Small;

  const imageView = (() => {
    if (logo)
      return (
        <LogoFrame>
          <ImageContainer>
            <HeImgix
              width={isMobile ? 70 : 123}
              height={isMobile ? 56 : 72}
              src={logo}
              imgixParams={{ fit: 'fill', fill: 'color', 'fill-color': 'fff' }}
              htmlAttributes={{
                alt: '',
                draggable: false,
                width: 'unset !important',
                height: 'unset !important',
              }}
            />
          </ImageContainer>
        </LogoFrame>
      );
    else if (image) return <Logo image={image} />;
    else return undefined;
  })();

  const logoNode = featured ? <FeaturedTag>{imageView}</FeaturedTag> : imageView;

  const isTelehealthOnly = tags.includes(PracticeTag.Telehealth);

  const dentalPricingBracket = useMemo(() => getDentalPricingBracket(treatments), [treatments]);

  if (dentalPricingBracket) clickTracking.searchCardPriceBracket = dentalPricingBracket;

  const importedAt =
    !!treatments && treatments?.length > 0 && treatments[0] && treatments[0].importedAt
      ? new Date(treatments[0].importedAt)
      : undefined;

  useAnalytics({
    resultsData: {
      // we leave it as undefined to let other cards potentially override it
      hasPricing: dentalPricingBracket ? true : undefined,
    },
  });

  const providerData = filters.insurance
    ? PROVIDERS.find(data => data.provider === filters.insurance)
    : undefined;

  const showBupaGapFree = preferredProviderPrograms.find(data => data.name === BUPA_PLATINUM);

  const urlContext: UrlContext = {
    specialty: searchSpecialty,
    vaccineType,
  };

  const urlInContext = addQueryParams(url, urlContext);

  const tagLabels = useMemo(() => {
    const labels = tagFactory(tags, filters.topic?.specialist ?? undefined);
    return filterNulls(labels);
  }, [tags, filters.topic?.specialist]);

  return (
    <Card>
      <CardBody>
        <Layout
          logo={logoNode}
          specialties={<Subheading>{formatSpecialties(specialties)}</Subheading>}
          practiceDetails={
            <>
              <PracticeDetails practice={practice} />
              {providerData && (providerData.gapFree || showBupaGapFree) && (
                <DentalPricingGapFree />
              )}
              {dentalPricingBracket &&
                !showBupaGapFree &&
                (!providerData || !providerData.gapFree) && (
                  <DentalPricing
                    importedAt={importedAt}
                    pricingBracket={dentalPricingBracket}
                    isVerified={practice.tags.includes(PracticeTag.PricingVerified)}
                  />
                )}
            </>
          }
          guts={
            <>
              <ProfileName href={urlInContext} trackingData={clickTracking}>
                {name}
              </ProfileName>

              {address && !isTelehealthOnly && (
                <Address
                  url={makePracticeContactUrl(url)}
                  address={address}
                  distance={(rankingInfo?.geoDistance ?? 0) / 1000}
                />
              )}
            </>
          }
          tags={tagLabels}
        />
      </CardBody>

      <Appointments
        appointmentSummary={appointmentSummary}
        isTelehealthOnly={isTelehealthOnly}
        barType={bookingBar}
        id={id}
        name={name}
        phone={phoneNumber}
        profileUrl={urlInContext}
        searchSpecialty={searchSpecialty ?? specialties[0] ?? ''}
        trackingData={clickTracking}
        timezone={timezone}
        type="practice"
      />
    </Card>
  );
};
